import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';

import QuickActionPanel from '../../components/ui/QuickActionPanel';
import MemberFilters from './components/MemberFilters';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import MemberTable from './components/MemberTable';
import MemberCards from './components/MemberCards';
import MemberProfileModal from './components/MemberProfileModal';

const MemberManagement = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    membershipStatus: 'all',
    paymentStatus: 'all',
    membershipType: 'all',
    lastVisit: 'all',
    minAge: '',
    maxAge: '',
    gender: 'all'
  });

  const mockMembers = [
  {
    id: 1,
    name: "Michael Rodriguez",
    email: "michael.rodriguez@email.com",
    phone: "+1 (555) 123-4567",
    address: "123 Fitness Street, Los Angeles, CA 90001",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa9e44fc-1763295892954.png",
    avatarAlt: "Professional headshot of Hispanic man with short black hair wearing navy blue athletic shirt",
    memberId: "GYM-2024-001",
    membershipType: "Annual Premium",
    status: "active",
    paymentStatus: "paid",
    lastCheckIn: "2026-01-25T08:30:00",
    expiryDate: "2026-12-31T23:59:59",
    joinDate: "2024-01-15T00:00:00",
    dateOfBirth: "1988-05-12T00:00:00",
    gender: "male",
    monthlyAmount: 89.99,
    nextBillingDate: "2026-02-01T00:00:00",
    flags: [
    { icon: "AlertTriangle", label: "Medical Note" }],

    emergencyContact: {
      name: "Sarah Rodriguez",
      relationship: "Spouse",
      phone: "+1 (555) 123-4568"
    },
    medicalNotes: "Previous knee injury - avoid high-impact exercises. Cleared for low-impact cardio and strength training.",
    membershipHistory: [
    { plan: "Monthly Basic", startDate: "2024-01-15T00:00:00", endDate: "2024-06-15T00:00:00", status: "completed" },
    { plan: "Annual Premium", startDate: "2024-06-16T00:00:00", endDate: "2026-12-31T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 89.99, date: "2026-01-01T00:00:00", status: "paid" },
    { amount: 89.99, date: "2025-12-01T00:00:00", status: "paid" },
    { amount: 89.99, date: "2025-11-01T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2024-01-15T00:00:00", status: "signed" },
    { name: "PAR-Q Form", signedDate: "2024-01-15T00:00:00", status: "signed" },
    { name: "Liability Waiver", signedDate: "2024-01-15T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 287,
      monthlyCheckIns: 18,
      streakDays: 12
    },
    recentActivity: [
    { action: "Checked in at Main Gym", timestamp: "2026-01-25T08:30:00", icon: "LogIn" },
    { action: "Booked HIIT Class", timestamp: "2026-01-24T14:20:00", icon: "Calendar" },
    { action: "Completed Workout Program", timestamp: "2026-01-23T18:45:00", icon: "CheckCircle2" }]

  },
  {
    id: 2,
    name: "Emily Chen",
    email: "emily.chen@email.com",
    phone: "+1 (555) 234-5678",
    address: "456 Wellness Avenue, San Francisco, CA 94102",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1735d4699-1763294722624.png",
    avatarAlt: "Professional headshot of Asian woman with long black hair wearing white fitness top",
    memberId: "GYM-2024-002",
    membershipType: "Monthly Premium",
    status: "active",
    paymentStatus: "paid",
    lastCheckIn: "2026-01-24T17:15:00",
    expiryDate: "2026-02-28T23:59:59",
    joinDate: "2023-08-20T00:00:00",
    dateOfBirth: "1992-09-18T00:00:00",
    gender: "female",
    monthlyAmount: 79.99,
    nextBillingDate: "2026-02-01T00:00:00",
    flags: [],
    emergencyContact: {
      name: "David Chen",
      relationship: "Brother",
      phone: "+1 (555) 234-5679"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Monthly Premium", startDate: "2023-08-20T00:00:00", endDate: "2026-02-28T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 79.99, date: "2026-01-01T00:00:00", status: "paid" },
    { amount: 79.99, date: "2025-12-01T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2023-08-20T00:00:00", status: "signed" },
    { name: "PAR-Q Form", signedDate: "2023-08-20T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 412,
      monthlyCheckIns: 22,
      streakDays: 8
    },
    recentActivity: [
    { action: "Checked in at Main Gym", timestamp: "2026-01-24T17:15:00", icon: "LogIn" },
    { action: "Attended Yoga Class", timestamp: "2026-01-24T17:30:00", icon: "Calendar" }]

  },
  {
    id: 3,
    name: "James Thompson",
    email: "james.thompson@email.com",
    phone: "+1 (555) 345-6789",
    address: "789 Health Boulevard, New York, NY 10001",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15f002d89-1763294705593.png",
    avatarAlt: "Professional headshot of Caucasian man with short brown hair wearing black athletic shirt",
    memberId: "GYM-2025-003",
    membershipType: "Class-Based (20 sessions)",
    status: "active",
    paymentStatus: "pending",
    lastCheckIn: "2026-01-23T19:45:00",
    expiryDate: "2026-06-30T23:59:59",
    joinDate: "2025-03-10T00:00:00",
    dateOfBirth: "1985-11-25T00:00:00",
    gender: "male",
    monthlyAmount: 0,
    nextBillingDate: "2026-02-15T00:00:00",
    flags: [
    { icon: "Clock", label: "Payment Due" }],

    emergencyContact: {
      name: "Lisa Thompson",
      relationship: "Wife",
      phone: "+1 (555) 345-6790"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Class-Based (20 sessions)", startDate: "2025-03-10T00:00:00", endDate: "2026-06-30T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 199.99, date: "2025-12-15T00:00:00", status: "paid" },
    { amount: 199.99, date: "2025-09-15T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2025-03-10T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 156,
      monthlyCheckIns: 14,
      streakDays: 5
    },
    recentActivity: [
    { action: "Checked in at Main Gym", timestamp: "2026-01-23T19:45:00", icon: "LogIn" },
    { action: "Attended Strength Training", timestamp: "2026-01-23T20:00:00", icon: "Calendar" }]

  },
  {
    id: 4,
    name: "Sophia Martinez",
    email: "sophia.martinez@email.com",
    phone: "+1 (555) 456-7890",
    address: "321 Fitness Lane, Miami, FL 33101",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15ef6ea0d-1763295960860.png",
    avatarAlt: "Professional headshot of Hispanic woman with curly brown hair wearing pink athletic top",
    memberId: "GYM-2025-004",
    membershipType: "Family Plan (4 members)",
    status: "frozen",
    paymentStatus: "paid",
    lastCheckIn: "2025-12-15T10:30:00",
    expiryDate: "2026-08-31T23:59:59",
    joinDate: "2024-09-01T00:00:00",
    dateOfBirth: "1990-03-08T00:00:00",
    gender: "female",
    monthlyAmount: 149.99,
    nextBillingDate: "2026-03-01T00:00:00",
    flags: [
    { icon: "Pause", label: "Account Frozen" }],

    emergencyContact: {
      name: "Carlos Martinez",
      relationship: "Husband",
      phone: "+1 (555) 456-7891"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Family Plan (4 members)", startDate: "2024-09-01T00:00:00", endDate: "2026-08-31T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 149.99, date: "2026-01-01T00:00:00", status: "paid" },
    { amount: 149.99, date: "2025-12-01T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2024-09-01T00:00:00", status: "signed" },
    { name: "Family Plan Addendum", signedDate: "2024-09-01T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 198,
      monthlyCheckIns: 0,
      streakDays: 0
    },
    recentActivity: [
    { action: "Account Frozen - Maternity Leave", timestamp: "2025-12-20T00:00:00", icon: "Pause" },
    { action: "Last Check-in", timestamp: "2025-12-15T10:30:00", icon: "LogIn" }]

  },
  {
    id: 5,
    name: "David Kim",
    email: "david.kim@email.com",
    phone: "+1 (555) 567-8901",
    address: "654 Strength Street, Seattle, WA 98101",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10cadd384-1763292115349.png",
    avatarAlt: "Professional headshot of Asian man with short black hair wearing gray athletic shirt",
    memberId: "GYM-2025-005",
    membershipType: "Corporate Plan",
    status: "active",
    paymentStatus: "paid",
    lastCheckIn: "2026-01-25T06:00:00",
    expiryDate: "2026-12-31T23:59:59",
    joinDate: "2025-01-05T00:00:00",
    dateOfBirth: "1987-07-22T00:00:00",
    gender: "male",
    monthlyAmount: 69.99,
    nextBillingDate: "2026-02-01T00:00:00",
    flags: [],
    emergencyContact: {
      name: "Jennifer Kim",
      relationship: "Sister",
      phone: "+1 (555) 567-8902"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Corporate Plan", startDate: "2025-01-05T00:00:00", endDate: "2026-12-31T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 69.99, date: "2026-01-01T00:00:00", status: "paid" },
    { amount: 69.99, date: "2025-12-01T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2025-01-05T00:00:00", status: "signed" },
    { name: "Corporate Plan Agreement", signedDate: "2025-01-05T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 245,
      monthlyCheckIns: 20,
      streakDays: 15
    },
    recentActivity: [
    { action: "Checked in at Main Gym", timestamp: "2026-01-25T06:00:00", icon: "LogIn" },
    { action: "Completed Morning Workout", timestamp: "2026-01-25T07:30:00", icon: "CheckCircle2" }]

  },
  {
    id: 6,
    name: "Olivia Johnson",
    email: "olivia.johnson@email.com",
    phone: "+1 (555) 678-9012",
    address: "987 Cardio Court, Boston, MA 02101",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1de624978-1763294904808.png",
    avatarAlt: "Professional headshot of Caucasian woman with blonde hair wearing blue athletic top",
    memberId: "GYM-2026-006",
    membershipType: "Trial (7 days)",
    status: "trial",
    paymentStatus: "paid",
    lastCheckIn: "2026-01-24T16:20:00",
    expiryDate: "2026-02-01T23:59:59",
    joinDate: "2026-01-20T00:00:00",
    dateOfBirth: "1995-12-03T00:00:00",
    gender: "female",
    monthlyAmount: 0,
    nextBillingDate: "2026-02-02T00:00:00",
    flags: [
    { icon: "Star", label: "Trial Member" }],

    emergencyContact: {
      name: "Robert Johnson",
      relationship: "Father",
      phone: "+1 (555) 678-9013"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Trial (7 days)", startDate: "2026-01-20T00:00:00", endDate: "2026-02-01T23:59:59", status: "active" }],

    paymentHistory: [],
    documents: [
    { name: "Trial Agreement", signedDate: "2026-01-20T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 4,
      monthlyCheckIns: 4,
      streakDays: 4
    },
    recentActivity: [
    { action: "Checked in at Main Gym", timestamp: "2026-01-24T16:20:00", icon: "LogIn" },
    { action: "Attended Spin Class", timestamp: "2026-01-24T16:30:00", icon: "Calendar" }]

  },
  {
    id: 7,
    name: "Marcus Williams",
    email: "marcus.williams@email.com",
    phone: "+1 (555) 789-0123",
    address: "147 Power Plaza, Chicago, IL 60601",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5e6bc1c-1763296049041.png",
    avatarAlt: "Professional headshot of African American man with short hair wearing red athletic shirt",
    memberId: "GYM-2024-007",
    membershipType: "Annual Basic",
    status: "expired",
    paymentStatus: "overdue",
    lastCheckIn: "2025-11-30T18:00:00",
    expiryDate: "2025-12-31T23:59:59",
    joinDate: "2024-01-01T00:00:00",
    dateOfBirth: "1983-04-15T00:00:00",
    gender: "male",
    monthlyAmount: 59.99,
    nextBillingDate: "2026-01-01T00:00:00",
    flags: [
    { icon: "AlertCircle", label: "Expired" },
    { icon: "DollarSign", label: "Payment Overdue" }],

    emergencyContact: {
      name: "Angela Williams",
      relationship: "Wife",
      phone: "+1 (555) 789-0124"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Annual Basic", startDate: "2024-01-01T00:00:00", endDate: "2025-12-31T23:59:59", status: "expired" }],

    paymentHistory: [
    { amount: 59.99, date: "2025-11-01T00:00:00", status: "paid" },
    { amount: 59.99, date: "2025-10-01T00:00:00", status: "paid" },
    { amount: 59.99, date: "2026-01-01T00:00:00", status: "failed" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2024-01-01T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 178,
      monthlyCheckIns: 0,
      streakDays: 0
    },
    recentActivity: [
    { action: "Payment Failed", timestamp: "2026-01-01T00:00:00", icon: "XCircle" },
    { action: "Membership Expired", timestamp: "2025-12-31T23:59:59", icon: "AlertCircle" }]

  },
  {
    id: 8,
    name: "Isabella Garcia",
    email: "isabella.garcia@email.com",
    phone: "+1 (555) 890-1234",
    address: "258 Flex Avenue, Austin, TX 78701",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d7334929-1763296602139.png",
    avatarAlt: "Professional headshot of Hispanic woman with long brown hair wearing green athletic top",
    memberId: "GYM-2025-008",
    membershipType: "Monthly Basic",
    status: "inactive",
    paymentStatus: "paid",
    lastCheckIn: "2025-10-12T14:30:00",
    expiryDate: "2026-02-28T23:59:59",
    joinDate: "2025-05-15T00:00:00",
    dateOfBirth: "1993-08-30T00:00:00",
    gender: "female",
    monthlyAmount: 49.99,
    nextBillingDate: "2026-02-01T00:00:00",
    flags: [
    { icon: "UserX", label: "Inactive 90+ days" }],

    emergencyContact: {
      name: "Miguel Garcia",
      relationship: "Brother",
      phone: "+1 (555) 890-1235"
    },
    medicalNotes: "",
    membershipHistory: [
    { plan: "Monthly Basic", startDate: "2025-05-15T00:00:00", endDate: "2026-02-28T23:59:59", status: "active" }],

    paymentHistory: [
    { amount: 49.99, date: "2026-01-01T00:00:00", status: "paid" },
    { amount: 49.99, date: "2025-12-01T00:00:00", status: "paid" }],

    documents: [
    { name: "Membership Agreement", signedDate: "2025-05-15T00:00:00", status: "signed" }],

    stats: {
      totalCheckIns: 45,
      monthlyCheckIns: 0,
      streakDays: 0
    },
    recentActivity: [
    { action: "Last Check-in", timestamp: "2025-10-12T14:30:00", icon: "LogIn" }]

  }];


  const [filteredMembers, setFilteredMembers] = useState(mockMembers);

  useEffect(() => {
    let result = [...mockMembers];

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter((member) =>
      member?.name?.toLowerCase()?.includes(searchLower) ||
      member?.email?.toLowerCase()?.includes(searchLower) ||
      member?.phone?.includes(searchLower)
      );
    }

    if (filters?.membershipStatus !== 'all') {
      result = result?.filter((member) => member?.status === filters?.membershipStatus);
    }

    if (filters?.paymentStatus !== 'all') {
      result = result?.filter((member) => member?.paymentStatus === filters?.paymentStatus);
    }

    if (filters?.membershipType !== 'all') {
      result = result?.filter((member) =>
      member?.membershipType?.toLowerCase()?.includes(filters?.membershipType?.toLowerCase())
      );
    }

    if (filters?.gender !== 'all') {
      result = result?.filter((member) => member?.gender === filters?.gender);
    }

    if (filters?.minAge) {
      const minAge = parseInt(filters?.minAge);
      result = result?.filter((member) => {
        const age = new Date()?.getFullYear() - new Date(member.dateOfBirth)?.getFullYear();
        return age >= minAge;
      });
    }

    if (filters?.maxAge) {
      const maxAge = parseInt(filters?.maxAge);
      result = result?.filter((member) => {
        const age = new Date()?.getFullYear() - new Date(member.dateOfBirth)?.getFullYear();
        return age <= maxAge;
      });
    }

    result?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'lastCheckIn' || sortConfig?.column === 'expiryDate') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortConfig?.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredMembers(result);
  }, [filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      membershipStatus: 'all',
      paymentStatus: 'all',
      membershipType: 'all',
      lastVisit: 'all',
      minAge: '',
      maxAge: '',
      gender: 'all'
    });
  };

  const handleSelectMember = (memberId, checked) => {
    if (checked) {
      setSelectedMembers((prev) => [...prev, memberId]);
    } else {
      setSelectedMembers((prev) => prev?.filter((id) => id !== memberId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMembers(filteredMembers?.map((m) => m?.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewMember = (memberId) => {
    const member = mockMembers?.find((m) => m?.id === memberId);
    setSelectedMemberProfile(member);
  };

  const handleBulkAction = (actionId) => {
    console.log(`Bulk action ${actionId} for members:`, selectedMembers);
  };

  const handleQuickAction = (actionId) => {
    if (actionId === 'add-member') {
      navigate('/member-registration');
    }
    console.log(`Quick action: ${actionId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="staff" userName="Admin User" />
      <div className="page-content-wrapper">
        <DashboardNavigation userRole="staff" />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
              Member Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage member accounts, billing, and service delivery
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table' ? 'bg-card shadow-sm' : 'hover:bg-card/50'}`
                }
                title="Table View">

                <Icon name="Table" size={18} />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-lg transition-colors ${
                viewMode === 'cards' ? 'bg-card shadow-sm' : 'hover:bg-card/50'}`
                }
                title="Card View">

                <Icon name="LayoutGrid" size={18} />
              </button>
            </div>

            <Button
              variant="default"
              onClick={() => navigate('/member-registration')}
              iconName="UserPlus"
              iconPosition="left">

              <span className="hidden sm:inline">Add Member</span>
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <MemberFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters} />


          <BulkActionsToolbar
            selectedCount={selectedMembers?.length}
            onAction={handleBulkAction} />


          <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Icon name="Users" size={20} color="var(--color-primary)" />
                <h2 className="text-base md:text-lg font-semibold text-foreground">
                  Members Directory
                </h2>
                <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  {filteredMembers?.length}
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left">

                <span className="hidden md:inline">Export</span>
              </Button>
            </div>

            {viewMode === 'table' ?
            <MemberTable
              members={filteredMembers}
              selectedMembers={selectedMembers}
              onSelectMember={handleSelectMember}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              sortConfig={sortConfig}
              onViewMember={handleViewMember} /> :


            <MemberCards
              members={filteredMembers}
              selectedMembers={selectedMembers}
              onSelectMember={handleSelectMember}
              onViewMember={handleViewMember} />

            }
          </div>
        </div>
      </main>
      <QuickActionPanel userRole="staff" onAction={handleQuickAction} />
      {selectedMemberProfile &&
      <MemberProfileModal
        member={selectedMemberProfile}
        onClose={() => setSelectedMemberProfile(null)}
        onUpdate={(updatedMember) => {
          console.log('Updated member:', updatedMember);
          setSelectedMemberProfile(null);
        }} />

      }
    </div>);

};

export default MemberManagement;